import '../../00group-overlay/dees-speechbubble/dees-speechbubble.js';
import * as plugins from '../../00plugins.js';
import * as interfaces from '../../interfaces/index.js';

import '../../00group-utility/dees-icon/dees-icon.js';

import {
  DeesElement,
  type TemplateResult,
  property,
  state,
  customElement,
  html,
  css,
  cssManager,
} from '@design.estate/dees-element';
import { demoFunc } from './dees-appui-secondarymenu.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

/**
 * Secondary navigation menu for sub-navigation within MainMenu views
 *
 * Supports 8 item types:
 * 1. Tab - selectable, stays highlighted (default)
 * 2. Action - executes without selection (blue)
 * 3. Danger Action - red styling with optional confirmation
 * 4. Filter - checkbox toggle
 * 5. Multi-Filter - collapsible box with multiple checkboxes
 * 6. Divider - visual separator
 * 7. Header - non-interactive label
 * 8. Link - opens URL
 */
@customElement('dees-appui-secondarymenu')
export class DeesAppuiSecondarymenu extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['App UI'];

  // INSTANCE

  /** Dynamic heading - typically shows the selected MainMenu item */
  @property({ type: String })
  accessor heading: string = 'Menu';

  /** Grouped items with collapse support - supports new ISecondaryMenuGroup */
  @property({ type: Array })
  accessor groups: interfaces.ISecondaryMenuGroup[] = [];

  /** Legacy flat list support for backward compatibility */
  @property({ type: Array })
  accessor selectionOptions: (interfaces.IMenuItem | { divider: true })[] = [];

  /** Currently selected tab item */
  @property({ type: Object })
  accessor selectedItem: interfaces.ISecondaryMenuItemTab | null = null;

  /** Internal state for collapsed groups */
  @state()
  accessor collapsedGroups: Set<string> = new Set();

  /** Internal state for collapsed multi-filters */
  @state()
  accessor collapsedMultiFilters: Set<string> = new Set();

  /** Render counter to force re-renders when items are mutated */
  @state()
  private accessor renderCounter: number = 0;

  /** Horizontal collapse state */
  @property({ type: Boolean, reflect: true })
  accessor collapsed: boolean = false;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      @media (prefers-reduced-motion: reduce) { *, *::before, *::after { transition: none !important; animation: none !important; } }

      :host {
        --sidebar-width-expanded: 240px;
        --sidebar-width-collapsed: 56px;
        --sidebar-bg: var(--dees-appui-chrome-bg);
        --sidebar-fg: var(--dees-color-text-secondary);
        --sidebar-fg-muted: var(--dees-color-text-muted);
        --sidebar-fg-active: var(--dees-color-text-primary);
        --sidebar-border: var(--dees-color-border-subtle);
        --sidebar-hover: var(--dees-color-hover);
        --sidebar-active: var(--dees-color-active);
        --sidebar-accent: var(--dees-color-accent-primary);
        --tooltip-bg: var(--dees-color-tooltip-bg);
        --tooltip-fg: var(--dees-color-tooltip-fg);

        /* Badge colors */
        --badge-default-bg: var(--dees-color-badge-default-bg);
        --badge-default-fg: var(--dees-color-badge-default-fg);
        --badge-success-bg: var(--dees-color-badge-success-bg);
        --badge-success-fg: var(--dees-color-badge-success-fg);
        --badge-warning-bg: var(--dees-color-badge-warning-bg);
        --badge-warning-fg: var(--dees-color-badge-warning-fg);
        --badge-error-bg: var(--dees-color-badge-error-bg);
        --badge-error-fg: var(--dees-color-badge-error-fg);

        /* Action colors */
        --action-primary: var(--dees-color-link);
        --action-primary-hover: var(--dees-color-link-hover);
        --action-danger: var(--dees-color-text-error);
        --action-danger-hover: color-mix(in srgb, var(--dees-color-text-error) 80%, var(--dees-color-text-primary));

        position: relative;
        display: block;
        height: 100%;
        width: var(--sidebar-width-expanded);
        background: var(--sidebar-bg);
        border-right: 1px solid var(--sidebar-border);
        font-family: var(--dees-font-family);
        user-select: none;
        transition: width var(--dees-transition-slow) var(--dees-ease-standard);
      }

      :host([collapsed]) {
        width: var(--sidebar-width-collapsed);
      }

      .maincontainer {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        position: relative;
      }

      /* Floating collapse toggle button */
      button { font: inherit; color: inherit; border: 0; background: transparent; text-align: left; cursor: pointer; }
      button:focus-visible { outline: 2px solid var(--dees-color-focus-ring); outline-offset: -2px; }
      .menuItem, .groupHeader, .multiFilter-header, .multiFilter-option { width: 100%; }
      .collapse-toggle { display: grid; place-items: center; flex-shrink: 0; width: 28px; height: 28px; padding: 0; border-radius: var(--dees-radius-md); color: var(--dees-color-text-secondary); }
      :host([collapsed]) .collapse-toggle { width: 48px; }
      .collapse-toggle:hover { background: var(--dees-color-hover); }
      @media (pointer: coarse) { .menuItem, .groupHeader, .multiFilter-header, .multiFilter-option { min-height: 44px; max-height: none; } .collapse-toggle { min-width: 44px; min-height: 44px; } }
      /* Header Section */
      .header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: var(--dees-appui-row-height);
        padding: 0 var(--dees-spacing-lg);
        flex-shrink: 0;
        box-sizing: border-box;
      }

      .header .heading {
        flex: 1;
        font-size: var(--dees-font-control-size);
        font-weight: 600;
        color: var(--sidebar-fg-active);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        transition: opacity var(--dees-transition-default) var(--dees-ease-standard), width var(--dees-transition-slow) var(--dees-ease-standard);
      }

      :host([collapsed]) .header {
        justify-content: center;
        padding: 0;
      }

      :host([collapsed]) .header .heading {
        display: none;
      }

      /* Scrollable Menu Section */
      .menuSection {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        overscroll-behavior: contain;
        padding: var(--dees-spacing-sm) 0;
      }

      .menuSection::-webkit-scrollbar {
        width: 6px;
      }

      .menuSection::-webkit-scrollbar-track {
        background: transparent;
      }

      .menuSection::-webkit-scrollbar-thumb {
        background: var(--dees-color-scrollbar-thumb);
        border-radius: var(--dees-radius-xs);
      }

      .menuSection::-webkit-scrollbar-thumb:hover {
        background: var(--dees-color-scrollbar-thumb-hover);
      }

      /* Menu Group */
      .menuGroup {
        padding: 0 var(--dees-spacing-sm);
        margin-bottom: var(--dees-spacing-xs);
      }

      :host([collapsed]) .menuGroup {
        padding: 0 4px;
      }

      .groupHeader {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 12px;
        cursor: pointer;
        border-radius: var(--dees-radius-md);
        transition: background var(--dees-transition-fast) var(--dees-ease-standard), opacity var(--dees-transition-default) var(--dees-ease-standard), max-height var(--dees-transition-slow) var(--dees-ease-standard);
        min-height: 28px;
        max-height: 28px;
      }

      /* Hover changes only the background fill (no border, no shift). */
      .groupHeader:hover {
        background: color-mix(in srgb, var(--dees-color-text-secondary) 6%, transparent);
      }

      .groupHeader .groupTitle {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        font-size: var(--dees-font-caption2-size);
        font-weight: 600;
        color: var(--dees-color-text-muted);
        text-transform: none;
        letter-spacing: normal;
        white-space: nowrap;
        overflow: hidden;
      }

      .groupHeader .groupTitle dees-icon {
        font-size: 16px;
        color: var(--dees-color-text-secondary);
      }

      .groupHeader .chevron {
        font-size: var(--dees-font-caption1-size);
        transition: transform var(--dees-transition-default) var(--dees-ease-standard);
        color: var(--dees-color-text-secondary);
        opacity: 0;
      }

      .groupHeader:hover .chevron, .groupHeader:focus-visible .chevron, .groupHeader.collapsed .chevron { opacity: 1; }
      @media (pointer: coarse) { .groupHeader .chevron { opacity: 1; } }

      .groupHeader.collapsed .chevron {
        transform: rotate(-90deg);
      }

      /* Hide group headers when horizontally collapsed */
      :host([collapsed]) .groupHeader {
        opacity: 0;
        min-height: 0;
        max-height: 0;
        padding: 0;
        margin: 0;
        pointer-events: none;
      }

      /* Group Items Container */
      .groupItems {
        overflow: hidden;
        transition: max-height 0.25s ease, opacity 0.2s ease, margin 0.25s ease;
        max-height: 1000px;
        opacity: 1;
        margin-bottom: 12px;
      }

      .groupItems.collapsed {
        max-height: 0;
        opacity: 0;
        margin-bottom: 0;
      }

      /* Always show items when horizontally collapsed (regardless of group collapse state) */
      :host([collapsed]) .groupItems {
        max-height: none;
        opacity: 1;
      }

      /* Menu Item Base */
      .menuItem {
        position: relative;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        margin: 2px 0;
        font-size: var(--dees-font-control-size);
        font-weight: 500;
        min-height: 32px;
        border-radius: var(--dees-radius-md);
        cursor: pointer;
        transition: background var(--dees-transition-fast) var(--dees-ease-standard), color var(--dees-transition-fast) var(--dees-ease-standard);
        color: var(--sidebar-fg);
      }

      .menuItem:hover {
        background: var(--sidebar-hover);
        color: var(--sidebar-fg-active);
      }

      .menuItem:active {
        background: var(--sidebar-active);
      }

      .menuItem.disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
      }

      /* Sidebar selection: systemBlue-tinted filled rounded rect with
         systemBlue label/icon (no left-edge bar). */
      .menuItem.selected {
        background: var(--dees-color-nav-selected);
        color: var(--dees-color-accent-primary);
        font-weight: 500;
      }

      .menuItem dees-icon {
        font-size: 16px;
        opacity: 0.7;
        flex-shrink: 0;
      }

      .menuItem.selected dees-icon {
        opacity: 1;
      }

      @media (pointer: coarse) {
        .menuItem, .groupHeader { min-height: 44px; max-height: none; }
        :host([collapsed]) .groupHeader { min-height: 0; max-height: 0; }
      }

      .menuItem .itemLabel {
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        transition: opacity 0.2s ease, width 0.25s ease;
      }

      /* Action Item Styles */
      .menuItem.action-primary {
        color: var(--action-primary);
      }

      .menuItem.action-primary:hover {
        color: var(--action-primary-hover);
        background: color-mix(in srgb, var(--action-primary) 10%, transparent);
      }

      .menuItem.action-primary dees-icon {
        opacity: 1;
      }

      .menuItem.action-danger {
        color: var(--action-danger);
      }

      .menuItem.action-danger:hover {
        color: var(--action-danger-hover);
        background: color-mix(in srgb, var(--action-danger) 10%, transparent);
      }

      .menuItem.action-danger dees-icon {
        opacity: 1;
      }

      /* Filter Item Styles */
      .menuItem.filter {
        justify-content: space-between;
      }

      .menuItem.filter .filter-checkbox {
        width: 16px;
        height: 16px;
        border: 2px solid var(--dees-color-border-strong);
        border-radius: var(--dees-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
        flex-shrink: 0;
      }

      .menuItem.filter .filter-checkbox.checked {
        background: var(--sidebar-accent);
        border-color: var(--sidebar-accent);
      }

      .menuItem.filter .filter-checkbox dees-icon {
        font-size: 12px;
        color: var(--dees-color-on-accent);
        opacity: 1;
      }

      .menuItem.filter.active {
        color: var(--sidebar-fg-active);
      }

      /* Multi-Filter Container */
      .multiFilter {
        margin: var(--dees-spacing-xs) 0;
        border: 1px solid var(--sidebar-border);
        border-radius: var(--dees-radius-lg);
        overflow: hidden;
        background: color-mix(in srgb, var(--dees-color-text-primary) 2%, transparent);
      }

      .multiFilter-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 12px;
        cursor: pointer;
        transition: background 0.15s ease;
      }

      .multiFilter-header:hover {
        background: var(--sidebar-hover);
      }

      .multiFilter-header .multiFilter-title {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        font-size: var(--dees-font-control-size-sm);
        font-weight: 500;
        color: var(--sidebar-fg-active);
      }

      .multiFilter-header .multiFilter-title dees-icon {
        font-size: 16px;
        opacity: 0.7;
      }

      .multiFilter-header .multiFilter-count {
        font-size: var(--dees-font-caption2-size);
        color: var(--sidebar-fg-muted);
        background: var(--badge-default-bg);
        padding: 2px 6px;
        border-radius: var(--dees-radius-sm);
      }

      .multiFilter-header .chevron {
        font-size: 12px;
        transition: transform 0.2s ease;
        color: var(--sidebar-fg-muted);
      }

      .multiFilter-header.collapsed .chevron {
        transform: rotate(-90deg);
      }

      .multiFilter-options {
        border-top: 1px solid var(--sidebar-border);
        overflow: hidden;
        transition: max-height 0.25s ease, opacity 0.2s ease;
        max-height: 500px;
        opacity: 1;
      }

      .multiFilter-options.collapsed {
        max-height: 0;
        opacity: 0;
        border-top: none;
      }

      .multiFilter-option {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 12px;
        cursor: pointer;
        transition: background 0.15s ease;
        font-size: 13px;
        color: var(--sidebar-fg);
      }

      .multiFilter-option:hover {
        background: var(--sidebar-hover);
        color: var(--sidebar-fg-active);
      }

      .multiFilter-option .option-checkbox {
        width: 16px;
        height: 16px;
        border: 2px solid var(--dees-color-border-strong);
        border-radius: var(--dees-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all var(--dees-transition-fast) var(--dees-ease-standard);
        flex-shrink: 0;
      }

      .multiFilter-option .option-checkbox.checked {
        background: var(--sidebar-accent);
        border-color: var(--sidebar-accent);
      }

      .multiFilter-option .option-checkbox dees-icon {
        font-size: 12px;
        color: var(--dees-color-on-accent);
      }

      .multiFilter-option dees-icon.option-icon {
        font-size: 14px;
        opacity: 0.7;
      }

      /* Divider */
      .menuDivider {
        height: 1px;
        background: var(--sidebar-border);
        margin: 8px 12px;
      }

      :host([collapsed]) .menuDivider {
        margin: 8px 4px;
      }

      /* Header/Label */
      .menuHeader {
        padding: 12px 12px 4px 12px;
        font-size: var(--dees-font-caption2-size);
        font-weight: 600;
        color: var(--sidebar-fg-muted);
        text-transform: none;
        letter-spacing: normal;
      }

      :host([collapsed]) .menuHeader {
        display: none;
      }

      /* Link Item */
      .menuItem.link .external-icon {
        font-size: 12px;
        opacity: 0.5;
        margin-left: auto;
      }

      /* Collapsed menu item styles */
      :host([collapsed]) .menuItem {
        justify-content: center;
        padding: 8px;
        gap: 0;
      }

      :host([collapsed]) .menuItem .itemLabel {
        opacity: 0;
        width: 0;
        position: absolute;
      }

      :host([collapsed]) .menuItem.selected::before {
        left: -4px;
      }

      :host([collapsed]) .menuItem .filter-checkbox,
      :host([collapsed]) .menuItem .external-icon {
        display: none;
      }

      :host([collapsed]) .multiFilter {
        display: none;
      }

      /* Badge Styles */
      .badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 18px;
        height: 18px;
        padding: 0 6px;
        font-size: 10px;
        font-weight: 600;
        border-radius: 9px;
        flex-shrink: 0;
      }

      .badge.default {
        background: var(--badge-default-bg);
        color: var(--badge-default-fg);
      }

      .badge.success {
        background: var(--badge-success-bg);
        color: var(--badge-success-fg);
      }

      .badge.warning {
        background: var(--badge-warning-bg);
        color: var(--badge-warning-fg);
      }

      .badge.error {
        background: var(--badge-error-bg);
        color: var(--badge-error-fg);
      }

      :host([collapsed]) .badge {
        display: grid; place-items: center; position: absolute; top: 0; right: 0;
        min-width: 14px; height: 14px; padding: 0 3px; margin: 0; font-size: 9px;
      }

      /* Legacy options container */
      .legacyOptions {
        padding: 0 8px;
      }

      /* Divider (legacy) */
      .divider {
        height: 1px;
        background: var(--sidebar-border);
        margin: 8px 12px;
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="maincontainer">
        <div class="header">
          <span class="heading">${this.heading}</span>
          <button type="button" class="collapse-toggle" aria-label=${`${this.collapsed ? 'Expand' : 'Collapse'} ${this.heading || 'section'} sidebar`} aria-expanded=${!this.collapsed} @click=${() => this.toggleCollapse()}>
            <dees-icon .icon=${this.collapsed ? 'lucide:PanelLeftOpen' : 'lucide:PanelLeftClose'} .iconSize=${16}></dees-icon>
          </button>
          <dees-speechbubble hint .disabled=${!this.collapsed} .text=${`Expand ${this.heading || 'section'} sidebar`}></dees-speechbubble>
        </div>
        <div class="menuSection">
          ${this.groups.length > 0
            ? this.renderGroups()
            : this.renderLegacyOptions()}
        </div>
      </div>

    `;
  }

  private renderGroups(): TemplateResult {
    return html`
      ${this.groups.map((group) => html`
        <div class="menuGroup">
          <button type="button" aria-expanded=${!this.collapsedGroups.has(group.name)}
            class="groupHeader ${this.collapsedGroups.has(group.name) ? 'collapsed' : ''}"
            @click="${() => this.toggleGroup(group.name)}"
          >
            <span class="groupTitle">
              ${group.iconName ? html`<dees-icon .icon="${this.normalizeIcon(group.iconName)}"></dees-icon>` : ''}
              ${group.name}
            </span>
            <dees-icon class="chevron" .icon="${'lucide:chevronDown'}"></dees-icon>
          </button>
          <div ?inert=${!this.collapsed && this.collapsedGroups.has(group.name)} class="groupItems ${this.collapsedGroups.has(group.name) ? 'collapsed' : ''}">
            ${group.items.map((item) => this.renderItem(item, group))}
          </div>
        </div>
      `)}
    `;
  }

  private renderItem(item: interfaces.ISecondaryMenuItem, group?: interfaces.ISecondaryMenuGroup): TemplateResult {
    // Check for hidden items
    if ('hidden' in item && item.hidden) {
      return html``;
    }

    // Determine item type
    const itemType = 'type' in item ? item.type : 'tab';

    switch (itemType) {
      case 'action':
        return this.renderActionItem(item as interfaces.ISecondaryMenuItemAction);
      case 'filter':
        return this.renderFilterItem(item as interfaces.ISecondaryMenuItemFilter);
      case 'multiFilter':
        return this.renderMultiFilterItem(item as interfaces.ISecondaryMenuItemMultiFilter);
      case 'divider':
        return this.renderDivider();
      case 'header':
        return this.renderHeader(item as interfaces.ISecondaryMenuItemHeader);
      case 'link':
        return this.renderLinkItem(item as interfaces.ISecondaryMenuItemLink);
      case 'tab':
      default:
        return this.renderTabItem(item as interfaces.ISecondaryMenuItemTab, group);
    }
  }

  private renderTabItem(item: interfaces.ISecondaryMenuItemTab, group?: interfaces.ISecondaryMenuGroup): TemplateResult {
    const isSelected = this.selectedItem?.key === item.key;
    const isDisabled = item.disabled === true;

    return html`
      <button type="button" ?disabled=${isDisabled} aria-label=${item.key} aria-current=${isSelected ? 'page' : 'false'}
        class="menuItem ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}"
        @click="${() => !isDisabled && this.selectTabItem(item, group)}"
      >
        ${item.iconName ? html`<dees-icon .icon="${this.normalizeIcon(item.iconName)}"></dees-icon>` : ''}
        <span class="itemLabel">${item.key}</span>
        ${item.badge !== undefined ? html`
          <span class="badge ${item.badgeVariant || 'default'}">${item.badge}</span>
        ` : ''}
      </button><dees-speechbubble hint .disabled=${!this.collapsed} .text=${item.key}></dees-speechbubble>
    `;
  }

  private renderActionItem(item: interfaces.ISecondaryMenuItemAction): TemplateResult {
    const variant = item.variant || 'primary';
    const isDisabled = item.disabled === true;

    return html`
      <button type="button" ?disabled=${isDisabled} aria-label=${item.key}
        class="menuItem action-${variant} ${isDisabled ? 'disabled' : ''}"
        @click="${() => !isDisabled && this.handleActionClick(item)}"
      >
        ${item.iconName ? html`<dees-icon .icon="${this.normalizeIcon(item.iconName)}"></dees-icon>` : ''}
        <span class="itemLabel">${item.key}</span>
      </button><dees-speechbubble hint .disabled=${!this.collapsed} .text=${item.key}></dees-speechbubble>
    `;
  }

  private renderFilterItem(item: interfaces.ISecondaryMenuItemFilter): TemplateResult {
    const isDisabled = item.disabled === true;

    return html`
      <button type="button" ?disabled=${isDisabled} aria-label=${item.key} aria-pressed=${item.active}
        class="menuItem filter ${item.active ? 'active' : ''} ${isDisabled ? 'disabled' : ''}"
        @click="${() => !isDisabled && this.handleFilterToggle(item)}"
      >
        ${item.iconName ? html`<dees-icon .icon="${this.normalizeIcon(item.iconName)}"></dees-icon>` : ''}
        <span class="itemLabel">${item.key}</span>
        <span aria-hidden="true" class="filter-checkbox ${item.active ? 'checked' : ''}">
          ${item.active ? html`<dees-icon .icon="${'lucide:check'}"></dees-icon>` : ''}
        </span>
      </button><dees-speechbubble hint .disabled=${!this.collapsed} .text=${item.key}></dees-speechbubble>
    `;
  }

  private renderMultiFilterItem(item: interfaces.ISecondaryMenuItemMultiFilter): TemplateResult {
    const isCollapsed = this.collapsedMultiFilters.has(item.key);
    const checkedCount = item.options.filter(opt => opt.checked).length;

    return html`
      <div class="multiFilter">
        <button type="button" aria-expanded=${!isCollapsed}
          class="multiFilter-header ${isCollapsed ? 'collapsed' : ''}"
          @click="${() => this.toggleMultiFilter(item.key)}"
        >
          <span class="multiFilter-title">
            ${item.iconName ? html`<dees-icon .icon="${this.normalizeIcon(item.iconName)}"></dees-icon>` : ''}
            ${item.key}
          </span>
          ${checkedCount > 0 ? html`<span class="multiFilter-count">${checkedCount}</span>` : ''}
          <dees-icon class="chevron" .icon="${'lucide:chevronDown'}"></dees-icon>
        </button>
        <div ?inert=${isCollapsed} class="multiFilter-options ${isCollapsed ? 'collapsed' : ''}">
          ${item.options.map(option => html`
            <button type="button" role="checkbox" aria-checked=${option.checked}
              class="multiFilter-option"
              @click="${() => this.handleMultiFilterOptionToggle(item, option.key)}"
            >
              <span aria-hidden="true" class="option-checkbox ${option.checked ? 'checked' : ''}">
                ${option.checked ? html`<dees-icon .icon="${'lucide:check'}"></dees-icon>` : ''}
              </span>
              ${option.iconName ? html`<dees-icon class="option-icon" .icon="${this.normalizeIcon(option.iconName)}"></dees-icon>` : ''}
              <span>${option.label}</span>
            </button>
          `)}
        </div>
      </div>
    `;
  }

  private renderDivider(): TemplateResult {
    return html`<div class="menuDivider"></div>`;
  }

  private renderHeader(item: interfaces.ISecondaryMenuItemHeader): TemplateResult {
    return html`<div class="menuHeader">${item.label}</div>`;
  }

  private renderLinkItem(item: interfaces.ISecondaryMenuItemLink): TemplateResult {
    const isExternal = item.external ?? item.href.startsWith('http');
    const isDisabled = item.disabled === true;

    return html`
      <button type="button" ?disabled=${isDisabled} aria-label=${item.key}
        class="menuItem link ${isDisabled ? 'disabled' : ''}"
        @click="${() => !isDisabled && this.handleLinkClick(item)}"
      >
        ${item.iconName ? html`<dees-icon .icon="${this.normalizeIcon(item.iconName)}"></dees-icon>` : ''}
        <span class="itemLabel">${item.key}</span>
        ${isExternal ? html`<dees-icon class="external-icon" .icon="${'lucide:externalLink'}"></dees-icon>` : ''}
      </button><dees-speechbubble hint .disabled=${!this.collapsed} .text=${item.key}></dees-speechbubble>
    `;
  }

  private renderLegacyOptions(): TemplateResult {
    return html`
      <div class="legacyOptions">
        ${this.selectionOptions.map((option) => {
          if ('divider' in option && option.divider) {
            return html`<div class="divider"></div>`;
          }
          const item = option as interfaces.IMenuItem;
          // Convert legacy IMenuItem to ISecondaryMenuItemTab
          const tabItem: interfaces.ISecondaryMenuItemTab = {
            key: item.key,
            iconName: item.iconName,
            action: item.action,
            badge: item.badge,
            badgeVariant: item.badgeVariant,
          };
          return this.renderTabItem(tabItem);
        })}
      </div>
    `;
  }

  // Helper to normalize icon names
  private normalizeIcon(iconName: string): string {
    return iconName.startsWith('lucide:') ? iconName : `lucide:${iconName}`;
  }

  private toggleGroup(groupName: string): void {
    const newCollapsed = new Set(this.collapsedGroups);
    if (newCollapsed.has(groupName)) {
      newCollapsed.delete(groupName);
    } else {
      newCollapsed.add(groupName);
    }
    this.collapsedGroups = newCollapsed;
  }

  private toggleMultiFilter(filterKey: string): void {
    const newCollapsed = new Set(this.collapsedMultiFilters);
    if (newCollapsed.has(filterKey)) {
      newCollapsed.delete(filterKey);
    } else {
      newCollapsed.add(filterKey);
    }
    this.collapsedMultiFilters = newCollapsed;
  }

  public toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    this.dispatchEvent(new CustomEvent('collapse-change', {
      detail: { collapsed: this.collapsed },
      bubbles: true,
      composed: true
    }));
  }

  private selectTabItem(item: interfaces.ISecondaryMenuItemTab, group?: interfaces.ISecondaryMenuGroup): void {
    this.selectedItem = item;
    item.action();

    this.dispatchEvent(new CustomEvent('item-select', {
      detail: { item, group },
      bubbles: true,
      composed: true
    }));
  }

  private async handleActionClick(item: interfaces.ISecondaryMenuItemAction): Promise<void> {
    // Handle confirmation if required
    if (item.confirmMessage) {
      const confirmed = window.confirm(item.confirmMessage);
      if (!confirmed) {
        return;
      }
    }

    await item.action();

    this.dispatchEvent(new CustomEvent('action-click', {
      detail: { item },
      bubbles: true,
      composed: true
    }));
  }

  private handleFilterToggle(item: interfaces.ISecondaryMenuItemFilter): void {
    const newActive = !item.active;
    // Update the item's active state
    item.active = newActive;
    item.onToggle(newActive);

    // Force re-render by incrementing the render counter
    this.renderCounter++;

    this.dispatchEvent(new CustomEvent('filter-toggle', {
      detail: { item, active: newActive },
      bubbles: true,
      composed: true
    }));
  }

  private handleMultiFilterOptionToggle(item: interfaces.ISecondaryMenuItemMultiFilter, optionKey: string): void {
    // Update the option's checked state
    const option = item.options.find(opt => opt.key === optionKey);
    if (option) {
      option.checked = !option.checked;
    }

    // Calculate the new selected keys
    const selectedKeys = item.options
      .filter(opt => opt.checked)
      .map(opt => opt.key);

    item.onChange(selectedKeys);

    // Force re-render by incrementing the render counter
    this.renderCounter++;

    this.dispatchEvent(new CustomEvent('multifilter-change', {
      detail: { item, selectedKeys },
      bubbles: true,
      composed: true
    }));
  }

  private handleLinkClick(item: interfaces.ISecondaryMenuItemLink): void {
    const isExternal = item.external ?? item.href.startsWith('http');

    if (isExternal) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = item.href;
    }

    this.dispatchEvent(new CustomEvent('link-click', {
      detail: { item },
      bubbles: true,
      composed: true
    }));
  }

  async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    await super.firstUpdated(_changedProperties);

    // Initialize collapsed state from group defaults
    if (this.groups.length > 0) {
      const initialCollapsed = new Set<string>();
      const initialMultiFilterCollapsed = new Set<string>();

      this.groups.forEach(group => {
        if (group.collapsed) {
          initialCollapsed.add(group.name);
        }

        // Check for collapsed multi-filters
        group.items.forEach(item => {
          if ('type' in item && item.type === 'multiFilter') {
            const multiFilter = item as interfaces.ISecondaryMenuItemMultiFilter;
            if (multiFilter.collapsed) {
              initialMultiFilterCollapsed.add(multiFilter.key);
            }
          }
        });
      });

      this.collapsedGroups = initialCollapsed;
      this.collapsedMultiFilters = initialMultiFilterCollapsed;

      // Auto-select first tab item if none selected
      if (!this.selectedItem) {
        for (const group of this.groups) {
          for (const item of group.items) {
            const itemType = 'type' in item ? item.type : 'tab';
            if (itemType === 'tab' || itemType === undefined) {
              const tabItem = item as interfaces.ISecondaryMenuItemTab;
              if (!tabItem.disabled) {
                this.selectTabItem(tabItem, group);
                return;
              }
            }
          }
        }
      }
    } else if (this.selectionOptions.length > 0) {
      // Legacy mode: select first non-divider option
      const firstOption = this.selectionOptions.find(opt => !('divider' in opt)) as interfaces.IMenuItem;
      if (firstOption && !this.selectedItem) {
        const tabItem: interfaces.ISecondaryMenuItemTab = {
          key: firstOption.key,
          iconName: firstOption.iconName,
          action: firstOption.action,
        };
        this.selectTabItem(tabItem);
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dees-appui-secondarymenu': DeesAppuiSecondarymenu;
  }
}
